import type {
  CollectionFilterDef,
  DateFilterDef,
  DateRangeFilterDef,
  DateTimeRangeFilterDef,
  PropertyFilterDef,
  PropertyFilterSelectOption,
  PropertyFilterTextOption,
  SegmentFilterDef,
  SwitchFilterDef,
  TextFilterDef,
} from './types';

export type FilterHelper = {
  collection: (filter: Omit<CollectionFilterDef, 'type'>) => CollectionFilterDef;
  date: (filter: Omit<DateFilterDef, 'type'>) => DateFilterDef;
  dateRange: (filter: Omit<DateRangeFilterDef, 'type'>) => DateRangeFilterDef;
  dateTimeRange: (filter: Omit<DateTimeRangeFilterDef, 'type'>) => DateTimeRangeFilterDef;
  property: (
    options: (PropertyFilterTextOption | PropertyFilterSelectOption)[],
    filter?: Omit<PropertyFilterDef, 'type' | 'options'>,
  ) => PropertyFilterDef;
  propertySelect: (option: Omit<PropertyFilterSelectOption, 'type'>) => PropertyFilterSelectOption;
  propertyText: (option: Omit<PropertyFilterTextOption, 'type'>) => PropertyFilterTextOption;
  segment: (filter: Omit<SegmentFilterDef, 'type'>) => SegmentFilterDef;
  switch: (filter: Omit<SwitchFilterDef, 'type'>) => SwitchFilterDef;
  text: (filter: Omit<TextFilterDef, 'type'>) => TextFilterDef;
};

export function createFilterHelper(): FilterHelper {
  return {
    text: filter => ({
      min: 0,
      autoCompleteFn: undefined,
      disabledClearable: false,
      ...filter,
      type: 'text',
    }),
    property: (options, filter = {}) => ({
      defaultOption: undefined,
      enableGlobalFilter: false,
      ...(filter ?? {}),
      options,
      type: 'property',
    }),
    collection: filter => ({
      enableMultiSelect: true,
      ...filter,
      type: 'collection',
    }),
    date: filter => ({
      maxDate: undefined,
      minDate: undefined,
      ...filter,
      type: 'date',
    }),
    dateRange: filter => ({
      maxDate: undefined,
      minDate: undefined,
      ...filter,
      type: 'dateRange',
    }),
    dateTimeRange: filter => ({
      maxDate: undefined,
      minDate: undefined,
      showSeconds: true,
      showTimezone: false,
      allowEqualDates: false,
      ...filter,
      type: 'dateTimeRange',
    }),
    propertyText: option => ({
      min: 0,
      autoCompleteFn: undefined,
      disabledClearable: false,
      enableMultiSelect: true,
      ...option,
      type: 'text',
    }),
    propertySelect: option => ({
      enableMultiSelect: true,
      ...option,
      type: 'select',
    }),
    segment: filter => ({
      enableMultiSelect: false,
      comparator: 'exact',
      ...filter,
      type: 'segment',
    }),
    switch: filter => ({
      defaultValue: false,
      label: undefined,
      enableMultiSelect: false,
      comparator: 'exact',
      ...filter,
      type: 'switch',
    }),
  };
}
